import React from 'react';
import i18n from '../../utilities/i18n';
import {
  Card,
  HStack,
  LoadingSpinner,
  Stack,
  VStack,
  Table,
  LoadingModal,
  StackProps,
} from '@adc/parallax-component-library';
import {
  IconPhosphorCaretUpRegular,
  IconPhosphorCaretDownRegular,
  IconPhosphorCaretUpDownRegular,
} from '@adc/parallax-icons/phosphor';
import MessageBanner from '../messageBanner/MessageBanner';
import { useTableSorting } from './hooks/useTableSorting';
import { useTableFiltering } from './hooks/useTableFiltering';
import { useTableSelection } from './hooks/useTableSelection';
import { TableToolbar } from './TableToolbar';

export enum SortDirection {
  Ascending = 'ascending',
  Descending = 'descending',
  Neutral = 'neutral',
}

const getSortIcon = (direction: SortDirection) => {
  switch (direction) {
    case SortDirection.Ascending:
      return <IconPhosphorCaretUpRegular size="XS" />;
    case SortDirection.Descending:
      return <IconPhosphorCaretDownRegular size="XS" />;
    case SortDirection.Neutral:
    default:
      return <IconPhosphorCaretUpDownRegular size="XS" color="$neutral.40" />;
  }
};

export type ColumnConfig = {
  label: string;
  dataKey: string;
  isSortable?: boolean;
  getSortValue?: (
    row: any,
  ) => string | number | boolean | Date | null | undefined;
};

export type CheckboxFilterItem = {
  id: string | number;
  label: string;
  value: boolean;
};

export type CheckboxFilterSection = {
  id: string | number;
  label: string;
  items: CheckboxFilterItem[];
};

export type ChildrenPlacement = 'start' | 'end';

type FilterProps = {
  enableFiltering?: boolean;
  checkboxFilters?: CheckboxFilterSection[];
  onFiltersChange?: (filters: CheckboxFilterSection[]) => void;
  onFiltersReset?: () => void;
};

type SelectionProps = {
  enableSelection?: boolean;
  enableSelectAll?: boolean;
  selectedRowKeys?: string[];
  onSelectionChange?: (selectedKeys: string[]) => void;
  rowKeyField?: string; // field name to use as the unique key for each row
  selectionActions?: React.ReactNode; // custom buttons/actions to show when selection is enabled
};

type DynamicTableProps = {
  isLoading: boolean;
  hasLoadingError: boolean;
  title?: string;
  description: string;
  columnData: ColumnConfig[];
  rowData: any[];
  showCard?: boolean;
  cellRenderer?: (dataKey: string, row: any) => React.ReactNode;
  children?: React.ReactElement;
  childrenPlacement?: ChildrenPlacement;
  toolbarProps?: Partial<StackProps>;
  testID?: string;
} & FilterProps &
  SelectionProps;

/**
 * A flexible, dynamic table component with sorting, filtering, and selection capabilities.
 * @param {boolean} isLoading - Indicates if the table is loading.
 * @param {boolean} hasLoadingError - Indicates if there was an error loading data.
 * @param {string} title - The title of the table.
 * @param {string} description - The description of the table.
 * @param {ColumnConfig[]} columnData - The column configurations for column labels, data access, and sorting logic.
 * @param {any[]} rowData - The data to display in the table.
 * @param {boolean} showCard - Whether to show the table inside a card. Defaults to true.
 * @param {(dataKey: string, row: any) => React.ReactNode} cellRenderer - Optional function to render the content of each cell.
 * @param {React.ReactElement} [children] - Optional children to render in the table header.
 * @param {ChildrenPlacement} [childrenPlacement] - Where to place children in the extra HStack. Defaults to 'start'.
 * @param {boolean} enableFiltering - Whether to enable filtering functionality.
 * @param {CheckboxFilterSection[]} checkboxFilters - The checkbox filter sections and items.
 * @param {(filters: CheckboxFilterSection[]) => void} onFiltersChange - Callback when filters change.
 * @param {() => void} onFiltersReset - Callback when filters are reset.
 * @param {boolean} enableSelection - Whether to enable row selection functionality.
 * @param {boolean} enableSelectAll - Whether to enable "select all" functionality when selection is enabled.
 * @param {string[]} selectedRowKeys - Array of selected row keys.
 * @param {(selectedKeys: string[]) => void} onSelectionChange - Callback when selection changes.
 * @param {string} rowKeyField - Field name to use as unique key for rows. Defaults to 'key'.
 * @param {React.ReactNode} selectionActions - Custom actions to show when selection is enabled.
 * @param {string} testID - TestID attribute of Parallax table
 * @returns {React.JSX.Element}
 */
export default function DynamicTable({
  isLoading,
  hasLoadingError,
  title = '',
  description,
  columnData,
  rowData,
  showCard = true,
  cellRenderer,
  children,
  childrenPlacement = 'end',
  toolbarProps,
  enableFiltering = false,
  checkboxFilters = [],
  onFiltersChange,
  onFiltersReset,
  enableSelection = false,
  enableSelectAll = true,
  selectedRowKeys = [],
  onSelectionChange,
  rowKeyField = 'key',
  selectionActions,
  testID,
}: DynamicTableProps) {
  // Custom hooks
  const {
    isSorting,
    displayRowData,
    sortColumnDataKey,
    sortDirection,
    onColumnHeaderPress,
  } = useTableSorting({ columnData, rowData });

  const {
    isFilterPopupOpen,
    setIsFilterPopupOpen,
    activeFiltersCount,
    onFilterChange,
    onResetFilters,
  } = useTableFiltering({
    enableFiltering,
    checkboxFilters,
    onFiltersChange,
    onFiltersReset,
  });

  const {
    isEveryVisibleRowSelected,
    onBatchSelectButtonPress,
    onRowSelectionToggle,
  } = useTableSelection({
    enableSelection,
    selectedRowKeys,
    rowData: displayRowData,
    rowKeyField,
    onSelectionChange,
  });

  const contents = (
    <VStack padding="$4" gap={'$2'} maxHeight="100%">
      {hasLoadingError ? (
        <MessageBanner
          type="danger"
          headerText={i18n.t('UnexpectedError.errorOccurred')}
          bodyText={i18n.t('UnexpectedError.description')}
          testID="dynamic-table-loading-error"
        />
      ) : null}
      {isLoading ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="dynamic-table-loading-spinner" />
        </VStack>
      ) : (
        <Table
          aria-label={description}
          minHeight={0}
          height="auto"
          maxHeight="100%"
          testID={testID}
        >
          <Table.Header>
            <Table.Row>
              {enableSelection && (
                <Table.HeaderCell key="select">
                  {''} {/* Empty header for selection column */}
                </Table.HeaderCell>
              )}
              {columnData.map((column) => {
                const isColumnSortable = column.isSortable ?? true;
                const isCurrentSortColumn =
                  column.dataKey === sortColumnDataKey;
                const sortIcon = !isColumnSortable
                  ? null
                  : getSortIcon(
                      isCurrentSortColumn
                        ? sortDirection
                        : SortDirection.Neutral,
                    );

                return (
                  <Table.HeaderCell key={column.dataKey}>
                    {isColumnSortable ? (
                      <Stack position="relative" width="100%">
                        <HStack
                          width="100%"
                          alignItems="center"
                          gap="$2"
                          cursor={isSorting ? 'not-allowed' : 'pointer'}
                        >
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {column.label}
                          </span>
                          {sortIcon}
                        </HStack>
                        <button
                          type="button"
                          disabled={isSorting}
                          onClick={() => onColumnHeaderPress(column.dataKey)}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            border: 'none',
                            background: 'transparent',
                            cursor: isSorting ? 'not-allowed' : 'pointer',
                          }}
                        />
                      </Stack>
                    ) : (
                      column.label
                    )}
                  </Table.HeaderCell>
                );
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isSorting && (
              <LoadingModal
                aria-label={i18n.t('Common.loading')}
                testID="dynamic-table-sorting-modal"
              />
            )}
            {displayRowData.map((row, index) => (
              <Table.Row
                testID={`dynamic-table-${row[rowKeyField] || index}`}
                key={row[rowKeyField] || index}
              >
                {enableSelection && (
                  <Table.CheckboxCell
                    key="select"
                    checked={selectedRowKeys.includes(String(row[rowKeyField]))}
                    onPress={() =>
                      onRowSelectionToggle(String(row[rowKeyField]))
                    }
                    scope="row"
                  />
                )}
                {columnData.map((column) =>
                  cellRenderer ? (
                    cellRenderer(column.dataKey, row)
                  ) : (
                    <Table.TextCell key={column.dataKey}>
                      {row[column.dataKey]}
                    </Table.TextCell>
                  ),
                )}
              </Table.Row>
            ))}
            {displayRowData.length === 0 && (
              <Table.Row>
                {enableSelection && (
                  <Table.TextCell key="select">{null}</Table.TextCell>
                )}
                {columnData.map((_, columnIndex) =>
                  columnIndex === 0 ? (
                    <Table.TextCell key={columnIndex}>
                      {i18n.t('Common.noData')}
                    </Table.TextCell>
                  ) : (
                    <Table.TextCell key={columnIndex}>{null}</Table.TextCell>
                  ),
                )}
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </VStack>
  );

  const extra = (
    <TableToolbar
      childrenPlacement={childrenPlacement}
      toolbarProps={toolbarProps}
      enableFiltering={enableFiltering}
      checkboxFilters={checkboxFilters}
      isFilterPopupOpen={isFilterPopupOpen}
      setIsFilterPopupOpen={setIsFilterPopupOpen}
      activeFiltersCount={activeFiltersCount}
      onFilterChange={onFilterChange}
      onResetFilters={onResetFilters}
      enableSelection={enableSelection}
      isEveryVisibleRowSelected={isEveryVisibleRowSelected}
      onBatchSelectButtonPress={onBatchSelectButtonPress}
      rowDataLength={displayRowData.length}
      selectionActions={selectionActions}
      enableSelectAll={enableSelectAll}
    >
      {children}
    </TableToolbar>
  );

  return showCard ? (
    <Card
      title={title}
      titleAriaLevel={1}
      titleTypeToken="$h4.strong"
      extra={extra}
    >
      {contents}
    </Card>
  ) : (
    <>
      {extra}
      {contents}
    </>
  );
}
